import { History } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { type Execution, ExecutionRow } from './ExecutionRow';

interface ExecutionHistoryProps {
  executions: Execution[];
  pagination?: {
    page: number;
    pageCount: number;
    total: number;
  };
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  onExecutionPress?: (execution: Execution) => void;
  loading?: boolean;
  fetching?: boolean;
  delay?: number;
}

function EmptyState({ t }: Readonly<{ t: (key: string) => string }>) {
  return (
    <Box
      borderRadius="lg"
      padding="xl"
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: 140 }}
    >
      <History size={28} color={colors.text.tertiary} strokeWidth={1.5} />
      <Text
        variant="body"
        color={colors.text.secondary}
        style={{ marginTop: 10, textAlign: 'center' }}
      >
        {t('workflow.history.emptyTitle')}
      </Text>
      <Text
        variant="caption"
        color={colors.text.tertiary}
        style={{ marginTop: 4, textAlign: 'center' }}
      >
        {t('workflow.history.emptyDescription')}
      </Text>
    </Box>
  );
}

export function ExecutionHistory({
  executions,
  pagination,
  onPreviousPage,
  onNextPage,
  onExecutionPress,
  loading,
  fetching,
  delay = 0,
}: Readonly<ExecutionHistoryProps>) {
  const { t } = useTranslation();

  return (
    <AnimatedCard delay={delay}>
      <View style={styles.card}>
        <View style={styles.header}>
          <HStack justify="between" align="center">
            <Text variant="h5" color={colors.text.primary} style={styles.title}>
              {t('workflow.history.title')}
            </Text>
            {pagination && pagination.total > 0 && (
              <Text variant="caption" color={colors.text.tertiary}>
                {pagination.total} {t('workflow.card.executions')}
              </Text>
            )}
          </HStack>
        </View>

        <View style={styles.body}>
          {loading && (
            <Box alignItems="center" justifyContent="center" paddingY="xl">
              <Text variant="body" color={colors.text.tertiary}>
                {t('workflow.history.loading')}
              </Text>
            </Box>
          )}
          {!loading && executions.length === 0 && <EmptyState t={t} />}
          {!loading && executions.length > 0 && (
            <VStack spacing="sm">
              {executions.map((execution, index) => (
                <ExecutionRow
                  key={execution.id}
                  execution={execution}
                  index={index}
                  onPress={onExecutionPress}
                  accessibilityHint={t('workflow.detail.openHint')}
                />
              ))}
            </VStack>
          )}
          {!loading && pagination && pagination.pageCount > 1 && (
            <HStack justify="between" align="center" style={styles.pagination}>
              <Button
                variant="secondary"
                size="sm"
                onPress={onPreviousPage}
                disabled={pagination.page === 1 || fetching}
                accessibilityLabel={t('workflow.history.previous')}
              >
                {t('workflow.history.previous')}
              </Button>
              <Text variant="caption" color={colors.text.secondary} style={styles.pageLabel}>
                {t('workflow.history.page')} {pagination.page} {t('workflow.history.of')}{' '}
                {pagination.pageCount}
              </Text>
              <Button
                variant="secondary"
                size="sm"
                onPress={onNextPage}
                disabled={pagination.page === pagination.pageCount || fetching}
                accessibilityLabel={t('workflow.history.next')}
              >
                {t('workflow.history.next')}
              </Button>
            </HStack>
          )}
        </View>
      </View>
    </AnimatedCard>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  body: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.md,
  },
  pagination: {
    marginTop: tokens.spacing.md,
    paddingTop: tokens.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
  pageLabel: {
    fontVariant: ['tabular-nums'],
  },
});
